import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ProfileService } from '../../services/profileService';
import { queryKeys } from '../../queries/keys';
import { SignedOutPanel } from '../../components/auth/SignedOutPanel';
import { useAuthStore } from '../../store/authStore';
import { resolveDisplayName, initialFor } from '../../components/auth/displayName';
import { getCentralAppUrl } from '../../config/authDomain';

/** One labelled fact. Renders an em dash rather than hiding a field we simply don't know. */
const Field: React.FC<{ label: string; value?: string | null; loading?: boolean }> = ({
  label,
  value,
  loading,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {loading ? (
      <Skeleton width={180} />
    ) : (
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value?.trim() ? value : '—'}
      </Typography>
    )}
  </Box>
);

/**
 * Account: who you are signed in as.
 *
 * Read-only on purpose. Identity lives in the central app, which is the system of record —
 * editing here would create two places a name can disagree, and the central API has no
 * update route to call anyway. So this shows what we know and links out to change it.
 */
export const AccountPage: React.FC = () => {
  const isSignedIn = useAuthStore(state => state.isAuthenticated);
  const user = useAuthStore(state => state.user);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.userProfile(),
    queryFn: () => ProfileService.getProfile(),
    enabled: isSignedIn,
    staleTime: 5 * 60_000,
  });

  if (!isSignedIn) {
    return (
      <Box sx={{ height: '100vh', bgcolor: 'background.default' }}>
        <SignedOutPanel
          what="your account"
          detail="Sign in to see the details on your profile and manage your sign-in."
        />
      </Box>
    );
  }

  // The token is the fallback everywhere the directory has nothing: it always carries a name
  // and email, so there is no state in which this page is blank.
  const displayName = resolveDisplayName(user);
  const email = data?.email ?? user?.email ?? null;
  const first = data?.firstName ?? user?.firstName ?? null;
  const last = data?.lastName ?? user?.lastName ?? null;

  const centralApp = getCentralAppUrl();

  return (
    <Box sx={{ height: '100vh', overflowY: 'auto', bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Account
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
          Your details come from your SmartAI account, which you use to sign in here and to the
          other apps. Changes made there apply everywhere.
        </Typography>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 3 }}>
            <Avatar
              src={data?.photoUrl ?? undefined}
              sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 26 }}
            >
              {initialFor(user)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {email ?? 'No email on file'}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <Field label="First name" value={first} loading={isLoading} />
            <Field label="Last name" value={last} loading={isLoading} />
            <Field label="Middle name" value={data?.middleName} loading={isLoading} />
            <Field label="Phone" value={data?.phoneNumber} loading={isLoading} />
            <Field label="Email" value={email} loading={isLoading} />
          </Box>
        </Paper>

        {data && !data.available && (
          <Alert severity="info" sx={{ mb: 3 }}>
            We couldn&apos;t reach your full profile just now, so this is showing what your
            sign-in tells us. Nothing is wrong with your account.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {centralApp && (
            <Button
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={`${centralApp}/profile`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Edit on SmartAI
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/settings/billing')}>
            Credit and Billing
          </Button>
          <Button variant="outlined" onClick={() => navigate('/settings/keys')}>
            API keys
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};
